'use client';

import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import type { FieldValues, UseFormReturn } from 'react-hook-form';

const input = tv({
  base: `
    file:text-foreground
    placeholder:text-muted-foreground
    selection:bg-primary
    selection:text-primary-foreground
    border-input
    focus-visible:ring-primary
    aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    aria-invalid:border-destructive
    flex
    h-10
    w-full
    min-w-0
    rounded-md
    border
    px-3
    bg-transparent
    text-base
    transition-all
    outline-none
    file:inline-flex
    file:h-7
    file:border-0
    file:bg-transparent
    file:text-sm
    file:font-medium
    focus-visible:ring-1
    focus-visible:ring-inset
    disabled:pointer-events-none
    disabled:cursor-not-allowed
    disabled:opacity-50
    md:text-sm
  `,
  variants: {
    hasPrefix: {
      true: 'rounded-l-none border-l-0',
    },
    hasPostfix: {
      true: 'rounded-r-none border-r-0',
    },
    withBorder: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      hasPrefix: true,
      withBorder: true,
      class: 'pl-3',
    },
    {
      hasPrefix: true,
      withBorder: false,
      class: 'pl-0',
    },
    {
      hasPostfix: true,
      withBorder: true,
      class: 'pr-3',
    },
    {
      hasPostfix: true,
      withBorder: false,
      class: 'pr-0',
    },
  ],
});

const inputContainer = tv({
  base: `
    flex
    h-10
    grow
    rounded-md
    border
    border-input
    bg-transparent
    transition-all
    focus-within:ring-1
    focus-within:ring-primary
    focus-within:ring-inset
    dark:aria-invalid:ring-destructive/40
    aria-invalid:ring-destructive/20
    aria-invalid:border-destructive
    disabled:pointer-events-none
    disabled:cursor-not-allowed
    disabled:opacity-50
  `,
});

const inputAddon = tv({
  base: `
    flex
    items-center
    justify-center
    px-3
    text-sm
    text-muted-foreground
    bg-transparent
    whitespace-nowrap
  `,
  variants: {
    position: {
      prefix: '',
      postfix: '',
    },
    withBorder: {
      true: '',
    },
  },
  compoundVariants: [
    {
      position: 'prefix',
      withBorder: true,
      class: 'border-r border-input',
    },
    {
      position: 'postfix',
      withBorder: true,
      class: 'border-l border-input',
    },
  ],
});

interface InputProps extends Omit<React.ComponentProps<'input'>, 'prefix'> {
  prefix?: React.ReactNode | ((formContext?: UseFormReturn<FieldValues>) => React.ReactNode);
  postfix?: React.ReactNode | ((formContext?: UseFormReturn<FieldValues>) => React.ReactNode);
  showBorders?: boolean;
  formContext?: UseFormReturn<FieldValues>;
  containerClassName?: string;
}

function Input({
  className,
  containerClassName,
  type,
  prefix,
  postfix,
  showBorders = false,
  formContext,
  ...props
}: InputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasPrefix = Boolean(prefix);
  const hasPostfix = Boolean(postfix);

  const handleAddonClick = () => {
    inputRef.current?.focus();
  };

  // If no prefix or postfix, render the simple input
  if (!hasPrefix && !hasPostfix) {
    return <input className={input({ className })} data-slot="input" type={type} {...props} />;
  }

  // Render input with prefix/postfix container
  return (
    <div className={inputContainer({ className: containerClassName })}>
      {prefix && (
        <div className={inputAddon({ position: 'prefix', withBorder: showBorders })} onClick={handleAddonClick}>
          {typeof prefix === 'function' ? prefix(formContext) : prefix}
        </div>
      )}
      <input
        className={input({
          hasPrefix,
          hasPostfix,
          withBorder: showBorders,
          className: twMerge('h-full! border-0 focus-visible:ring-0 focus-visible:ring-offset-0', className),
        })}
        data-slot="input"
        ref={inputRef}
        type={type}
        {...props}
      />
      {postfix && (
        <div className={inputAddon({ position: 'postfix', withBorder: showBorders })} onClick={handleAddonClick}>
          {typeof postfix === 'function' ? postfix(formContext) : postfix}
        </div>
      )}
    </div>
  );
}

export { input, inputContainer, inputAddon, Input };
export type { InputProps };
